import { noteGenerationSchema } from "@rosc/contracts";
import { z } from "zod";
import {
  assertNoObsoleteSubmission,
  discardObsoleteSubmissions,
} from "./obsolete-submissions";

export const NOTE_SUBMISSION_CHANGED = "rosc:note-submission-changed";
const identity = {
  version: z.literal(2),
  key: z.uuid(),
  body: noteGenerationSchema,
};
const schema = z.discriminatedUnion("kind", [
  z.strictObject({ ...identity, kind: z.literal("pending") }),
  z.strictObject({
    ...identity,
    kind: z.literal("accepted"),
    notePublicId: z.uuidv4().toLowerCase(),
  }),
]);
export type NoteSubmission = z.output<typeof schema>;
const storageKey = (account: string, parent: string): string =>
  `rosc:note-submission:${encodeURIComponent(account)}:${parent}`;
export function readNoteSubmission(
  account: string,
  parent: string,
): NoteSubmission | null {
  assertNoObsoleteSubmission(account, "rosc:note-submission:");
  const value = sessionStorage.getItem(storageKey(account, parent));
  if (value === null) return null;
  try {
    return schema.parse(JSON.parse(value));
  } catch {
    throw Error(
      "Saved note request cannot be read. Check the visit before starting another request.",
    );
  }
}
function save(account: string, parent: string, value: NoteSubmission): void {
  sessionStorage.setItem(storageKey(account, parent), JSON.stringify(value));
  window.dispatchEvent(new Event(NOTE_SUBMISSION_CHANGED));
}
export function prepareNoteSubmission(
  account: string,
  parent: string,
  body: unknown,
): NoteSubmission {
  const parsed = noteGenerationSchema.parse(body);
  const existing = readNoteSubmission(account, parent);
  if (existing) {
    if (JSON.stringify(existing.body) !== JSON.stringify(parsed))
      throw Error(
        "A saved note request exists. Retry its saved options or check the visit before starting another.",
      );
    return existing;
  }
  const pending: NoteSubmission = {
    version: 2,
    kind: "pending",
    key: crypto.randomUUID(),
    body: parsed,
  };
  save(account, parent, pending);
  return pending;
}
export function completeNoteSubmission(
  account: string,
  parent: string,
  submission: NoteSubmission,
  notePublicId: string,
): void {
  save(
    account,
    parent,
    schema.parse({ ...submission, version: 2, kind: "accepted", notePublicId }),
  );
}
export function abandonNoteSubmission(account: string, parent: string): void {
  discardObsoleteSubmissions(account, "rosc:note-submission:");
  sessionStorage.removeItem(storageKey(account, parent));
  window.dispatchEvent(new Event(NOTE_SUBMISSION_CHANGED));
}
export function beginNoteDraft(account: string, parent: string): void {
  if (readNoteSubmission(account, parent)?.kind === "accepted")
    abandonNoteSubmission(account, parent);
}
